import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  PageHeader,
  Pagination,
  TableSkeleton,
  generateTableFromSchema,
  useTable,
  Tag,
  EditPanel,
  Checkbox,
  Picto,
  Modal,
} from '@zolteam/zol-admin-ui';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

// Components
import BookingDetails from '../../components/organisms/bookingDetails/BookingDetails';
import BookingAddDocument from '../../components/organisms/bookingAddDocument/BookingAddDocument';
import BookingFilters from '../../components/organisms/bookingFilters/BookingFilters';
// services
import { useGetBookingListQuery } from '../../services/bookings/bookingsQueries';
import { duplicateBooking } from '../../services/bookings/bookingsService';
// utils
import { displayDestionationInfo } from '../../domain/airports/airportUseCases';
import { displayCustomerFullName, goToEditBooking } from '../../domain/bookings/bookingsUseCases';

// Services

/**
 * bookingList component.
 * @returns {JSX.Element}
 */
function BookingList() {
  // Hooks
  const navigate = useNavigate();
  const { t } = useTranslation();

  // json schema
  const UserListSchema = {
    $schema: 'https://json-schema.org/draft/2020-12/schema',
    type: 'object',
    title: 'Customer',
    properties: {},
    additionalProperties: false,
  };

  // -- search-params
  const [searchParam, setSearchParams] = useSearchParams();
  const pageNumber = searchParam.get('pageNumber') || 1;
  const pageSize = searchParam.get('pageSize') || 10;
  const statusFilter = searchParam.get('status') || '';
  const brokerFilter = searchParam.get('brokerId') || '';
  const customerFilter = searchParam.get('customerId') || '';
  const departureDateFilter = searchParam.get('departureDate') || '';
  const departureDateRangeStartFilter = searchParam.get('departureDateRangeStart') || '';
  const departureDateRangeEndFilter = searchParam.get('departureDateRangeEnd') || '';
  const idsFilter = searchParam.get('bookingIds') || '';
  const sort = searchParam.get('sort') || 'departureDateSort';
  const order = searchParam.get('order') || 'desc';

  // local state
  const [currentBooking, setCurrentBooking] = useState(null);
  const [isAddDocPanelOpen, setIsDocPanelOpen] = useState(false);
  const [bookingToCopy, setBookingToCopy] = useState(/** @type {Booking} */ (null));

  // hooks
  const {
    handlePaginationChange,
    handleColumnSort,
    countSelectedRows,
    dispatch: useTableDispatch,
    selectedRows,
  } = useTable({
    setParams:
    /**
     * Set the search params.
     * @param {object} params
     * @returns {void}
     */
    (params) => setSearchParams((searchParams) => {
      Object.entries(params).forEach(([key, value]) => {
        searchParams.set(key, value);
      });
      return searchParams;
    }),
    order,
    sort,
    // @ts-ignore
    statusFilter,
    brokerFilter,
    customerFilter,
    departureDateFilter,
    departureDateRangeStartFilter,
    departureDateRangeEndFilter,
    idsFilter,
    defaultSelectedRows: [],
  });

  // Services
  const duplicateBookingMutation = useMutation(duplicateBooking, {
    onSuccess: async (id) => {
      goToEditBooking(id?.toString());
    },
  });

  const {
    data: dataBookingList,
    isLoading: isLoadingDatabookingList,
    refetch,
  } = useGetBookingListQuery(
    {
      pageNumber: Number(pageNumber),
      pageSize: Number(pageSize),
      status: statusFilter,
      brokerId: brokerFilter,
      customerId: customerFilter,
      departureDate: departureDateFilter,
      departureDateRangeStart: departureDateRangeStartFilter,
      departureDateRangeEnd: departureDateRangeEndFilter,
      bookingIds: idsFilter,
      departureDateSort: order,
    },
    {
      keepPreviousData: true,
    },
  );

  // Handlers
  /**
   * Handle the click on the new content button.
   * @returns {void}
   */
  const handleNewContentClick = () => navigate('/bookings/new');
  /**
   * Handle the click on a table row.
   * @param {any} row
   * @returns {void}
   */
  const handleRowNavigate = (row) => {
    setCurrentBooking(row);
  };

  /**
   * Handle the close of the panel.
   * @returns {void}
   */
  const handleCloseDetailPanel = () => {
    setCurrentBooking(null);
    refetch();
  };

  /**
   * Open or close add Doc panel
   */
  const toggleAddDocPanel = () => {
    setIsDocPanelOpen((state) => {
      if (state) {
        useTableDispatch({ type: 'RESET_SELECTED_ROWS' });
      }
      return !state;
    });
  };

  /**
   * Check if a row is checked
   * @param {string} id
   * @returns {boolean}
   */
  const isRowChecked = (id) => Object.keys(selectedRows).includes(id);

  /**
   * Handle row selection
   * @param {Booking} row
   */
  const handleSelectRow = (row) => {
    if (isRowChecked(row.id?.toString())) {
      useTableDispatch({ type: 'DESELECT_ROW', payload: { id: row.id, singleSelection: false } });
    } else {
      useTableDispatch({ type: 'SELECT_ROW', payload: { id: row.id, singleSelection: false } });
    }
  };

  /**
   * Handle search param change in filter
   * @param {string} key
   * @param {string} value
   */
  const handleSearchParamChange = (key, value) => {
    if (searchParam.get('pageNumber') !== '1') {
      setSearchParams((searchParams) => {
        searchParams.set('pageNumber', '1');
        return searchParams;
      });
    }
    if (value) {
      setSearchParams((searchParams) => {
        searchParams.set(key, value);
        return searchParams;
      });
    } else {
      setSearchParams((searchParams) => {
        searchParams.delete(key);
        return searchParams;
      });
    }
  };

  /**
   * @param {Booking} booking
   */
  const handleCopy = (booking) => {
    setBookingToCopy(booking);
  };

  /**
   * Handle duplicate booking
   * @param {boolean} withBrief
   */
  const handleDuplicate = (withBrief) => {
    if (bookingToCopy) {
      duplicateBookingMutation.mutate({
        bookingToCopy,
        withBrief,
      });
    }
  };

  return (
    <div>
      {/* Copy Modal */}
      { bookingToCopy ? (
        <Modal
          id="copyBookingModal"
          isOpen={!!bookingToCopy}
          onClose={() => setBookingToCopy(null)}
          title={t('bookingList.copyModal.title')}
        >
          <div className="d-flex f-column gap-m justify-center">
            <p className="p1-default ws-prewrap">
              {t('bookingList.copyModal.description')}
            </p>
            <div className="d-flex f-row justify-end gap-s">
              <Button
                color="neutral00"
                onClick={() => setBookingToCopy(null)}
              >
                {t('bookingList.copyModal.actions.cancel')}
              </Button>
              <Button
                onClick={() => handleDuplicate(false)}
              >
                {t('bookingList.copyModal.actions.withoutBrief')}
              </Button>
              {bookingToCopy?.flightBriefUuid ? (
                <Button
                  onClick={() => handleDuplicate(true)}
                >
                  {t('bookingList.copyModal.actions.withBrief')}
                </Button>
              ) : null}
            </div>
          </div>
        </Modal>
      ) : null}
      <Box direction="column" gap="s" className="padding-l">
        {/* header */}
        <PageHeader
          title={(
            <>
              <h1 className="h1-default" style={{ fontWeight: 'bold' }}>
                {t('bookingList.title')}
              </h1>
              {dataBookingList?.data?.length && dataBookingList?.total ? (
                <span className="font-color-neutral-500">
                  {t('bookingList.nbRecords', { count: dataBookingList.total })}
                </span>
              ) : null}
            </>
        )}
          actions={(
            <Button icon="add" onClick={handleNewContentClick}>
              {t('bookingList.actions.new')}
            </Button>
        )}
        />
        {/* booking detail view */}
        <EditPanel
          id="bookingDetail"
          onClose={handleCloseDetailPanel}
          isOpen={!!currentBooking}
        >
          {currentBooking
            ? (
              <BookingDetails
                bookingId={currentBooking.id}
                handleClosePanel={handleCloseDetailPanel}
              />
            ) : null}
        </EditPanel>
        {/* add document view */}
        <EditPanel
          id="addDocument"
          onClose={toggleAddDocPanel}
          isOpen={isAddDocPanelOpen}
        >
          <BookingAddDocument
            onSuccess={toggleAddDocPanel}
            bookingIds={Object.keys(selectedRows)?.map((id) => Number(id))}
          />
        </EditPanel>

        <BookingFilters
          searchParam={searchParam}
          handleSearchParamChange={handleSearchParamChange}
          statusFilter={statusFilter}
          brokerFilter={brokerFilter}
          customerFilter={customerFilter}
        />

        {countSelectedRows ? (
          <Box align="flex-end" justify="space-between" fullWidth>
            {t('bookingList.selection.nbBookingSelected', { count: countSelectedRows })}
            <Button
              color="neutral00"
              icon="add-bold"
              onClick={toggleAddDocPanel}
            >
              {t('bookingList.actions.addDocument')}
            </Button>
          </Box>
        ) : null}

        {isLoadingDatabookingList
          ? <TableSkeleton />
          : generateTableFromSchema(
            {
              JSONSchema: UserListSchema,
              data: dataBookingList?.data || [],
              sort: {
                order,
                active: sort,
                handleColumnSort,
              },
              navigate: {
              // @ts-ignore
                handleRowNavigate,
              },
              labelNoResult: t('bookingList.noResult'),
              customColumns: [
                {
                  key: 'selection',
                  description: '',
                  sortable: false,
                  position: 'start',
                  renderHead: () => <Picto icon="gallery-add" />,
                  renderCell: (/** @type {Booking} */row) => (
                    <div className="cell">
                      <Checkbox
                        id={row.id?.toString()}
                        checked={isRowChecked(row.id?.toString())}
                        size="small"
                    // @ts-ignore
                        onChange={() => handleSelectRow(row)}
                      />
                    </div>
                  ),
                },
                {
                  key: 'departureDateSort',
                  description: '',
                  sortable: true,
                  position: 'end',
                  renderHead: () => {
                    if (sort === 'departureDateSort') {
                      return (
                        <>
                          {t('bookingList.columns.departure')}
                          <Picto icon={order === 'asc' ? 'arrow-up-bulk' : 'arrow-down-bulk'} />
                        </>
                      );
                    }
                    return t('bookingList.columns.departure');
                  },
                  renderCell: (/** @type {Booking} */row) => (
                    <button className="cell" type="button" onClick={() => handleRowNavigate(row)}>
                      {displayDestionationInfo({
                        airportCode: row.departure.airport.airportCode,
                        airportCity: row.departure.airport.city,
                        date: row.departure.date,
                        time: row.departure.time,
                      })}
                    </button>
                  ),
                },
                {
                  key: 'arrival',
                  description: '',
                  sortable: false,
                  position: 'end',
                  renderHead: () => t('bookingList.columns.arrival'),
                  renderCell: (/** @type {Booking} */row) => (
                    <button className="cell" type="button" onClick={() => handleRowNavigate(row)}>
                      {displayDestionationInfo({
                        airportCode: row.arrival.airport.airportCode,
                        airportCity: row.arrival.airport.city,
                        date: row.arrival.date,
                        time: row.arrival.time,
                      })}
                    </button>
                  ),
                },
                {
                  key: 'customer',
                  description: '',
                  sortable: false,
                  position: 'end',
                  renderHead: () => t('bookingList.columns.customer'),
                  renderCell: (/** @type {Booking} */row) => (
                    <button className="cell" type="button" onClick={() => handleRowNavigate(row)}>
                      {displayCustomerFullName(row.customer)}
                    </button>
                  ),
                },
                {
                  key: 'broker',
                  description: '',
                  sortable: false,
                  position: 'end',
                  renderHead: () => t('bookingList.columns.broker'),
                  renderCell: (/** @type {Booking} */row) => (
                    <button className="cell" type="button" onClick={() => handleRowNavigate(row)}>
                      {`${row.broker?.firstname || ''} ${row.broker?.lastname?.charAt(0) || '-'}`}
                    </button>
                  ),
                },
                {
                  key: 'status',
                  description: '',
                  sortable: true,
                  position: 'end',
                  renderHead: () => {
                    if (sort === 'status') {
                      return (
                        <>
                          {t('bookingList.columns.status')}
                          <Picto icon={order === 'asc' ? 'arrow-up-bulk' : 'arrow-down-bulk'} />
                        </>
                      );
                    }
                    return t('bookingList.columns.status');
                  },
                  renderCell: (/** @type {Booking} */row) => (
                    <button className="cell" type="button" onClick={() => handleRowNavigate(row)}>
                      <Tag label={t(`bookingStatus.${row.status}`)} />
                    </button>
                  ),
                },
                {
                  key: 'copy',
                  description: '',
                  sortable: false,
                  position: 'end',
                  renderHead: () => '',
                  renderCell: (/** @type {Booking} */row) => (
                    <div>
                      <Button
                        size="small"
                        onClick={() => handleCopy(row)}
                        className="cell"
                      >
                        {t('bookingList.actions.copy')}
                      </Button>
                    </div>
                  ),
                },
              ],
            },
          )}

        {dataBookingList?.data.length && dataBookingList?.total ? (
          <Pagination
            recordsCount={dataBookingList.total}
            pageNumber={+pageNumber}
            pageSize={+pageSize}
            onPageNumberChange={handlePaginationChange}
            perPageResultLabel={t('pagination.resultPerPage')}
          />
        ) : null}

      </Box>
    </div>
  );
}

export default BookingList;
